/**
 * Accessibility fixes for the predictive-search popdown, which Nosto renders
 * client-side in place of this theme's own markup (div[data-nosto-query]
 * .ns-autocomplete-element results inside div#predictive-search-results,
 * nested in the header search popdown). Not fixable from this repo since
 * Nosto owns that markup; this progressively enhances it instead:
 *
 * 1. Suggestion items have no tabindex/role at all -- unreachable by Tab and
 *    invisible to assistive tech. Made focusable, with click-to-select driven
 *    from the keyboard.
 * 2. #predictive-search-results (the theme's own aria-controls target,
 *    reused by Nosto for its own render) never gets a role, leaving Nosto's
 *    role="option" items with no owning listbox, and the combobox's
 *    aria-expanded stays "false" even with results showing. Both are
 *    additive attribute fixes on elements the theme already owns.
 * 3. .predictive-search-status (the theme's live region for this popdown)
 *    is only ever wired up for the theme's own predictive-search section,
 *    which Nosto's render bypasses entirely, so it never receives a result
 *    count. Populated here instead, debounced so rapid typing doesn't spam
 *    announcements, and only when the count actually changes.
 */
(function () {
  'use strict';

  var SUGGESTION_SELECTOR = '[data-nosto-query]';
  var COMBOBOX_SELECTOR = 'input[role="combobox"][aria-controls="predictive-search-results"]';
  var RESULT_ITEM_SELECTOR = '.predictive-search__item, .predictive-search__product-item';
  var STATUS_DEBOUNCE = 400;

  function makeFocusable(el) {
    if (el.tabIndex < 0) el.setAttribute('tabindex', '0');
    if (!el.hasAttribute('role')) el.setAttribute('role', 'option');

    // Avoid a second Tab stop if Nosto's inner keyword element is itself focusable.
    var keyword = el.querySelector('[data-nosto-element="keyword"]');
    if (keyword && keyword.tabIndex >= 0) {
      keyword.setAttribute('tabindex', '-1');
    }
  }

  function scan(root) {
    if (!root.querySelectorAll) return;
    if (root.nodeType === 1 && root.matches(SUGGESTION_SELECTOR)) makeFocusable(root);
    root.querySelectorAll(SUGGESTION_SELECTOR).forEach(makeFocusable);
  }

  // input -> { lastAnnouncedCount, timer }. Each search-form instance (mobile,
  // desktop) has its own combobox, results container and status region.
  var comboboxState = new WeakMap();

  function resultsContainerFor(input) {
    var form = input.closest('form');
    return form && form.querySelector('#predictive-search-results');
  }

  function statusFor(input) {
    var form = input.closest('form');
    return form && form.querySelector('.predictive-search-status');
  }

  function countResults(container) {
    return container ? container.querySelectorAll(RESULT_ITEM_SELECTOR).length : 0;
  }

  function stateFor(input) {
    var state = comboboxState.get(input);
    if (!state) {
      state = { lastAnnouncedCount: null, timer: null };
      comboboxState.set(input, state);
    }
    return state;
  }

  function resetCombobox(input) {
    input.setAttribute('aria-expanded', 'false');
    var state = stateFor(input);
    window.clearTimeout(state.timer);
    state.lastAnnouncedCount = null;
    var status = statusFor(input);
    if (status) {
      status.removeAttribute('aria-hidden');
      status.textContent = '';
    }
  }

  function isPopdownOpen(input) {
    var details = input.closest('details');
    return details ? details.open : true;
  }

  function syncCombobox(input) {
    var container = resultsContainerFor(input);

    if (container) {
      if (!container.hasAttribute('role')) container.setAttribute('role', 'listbox');
      if (!(container.getAttribute('aria-label') || '').trim()) {
        container.setAttribute('aria-label', 'Search suggestions');
      }
    }

    // A closed popdown -- or one whose query was just cleared -- has no
    // active results, regardless of what Nosto still has sitting in the DOM
    // (it hides the container but doesn't remove the stale items). Otherwise
    // the very next unrelated mutation anywhere on the page re-asserts
    // aria-expanded="true" on a hidden/empty combobox.
    var open = isPopdownOpen(input) && input.value.trim() !== '';
    var count = open ? countResults(container) : 0;
    input.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');

    var state = stateFor(input);
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(function () {
      if (count === state.lastAnnouncedCount) return;
      state.lastAnnouncedCount = count;

      var status = statusFor(input);
      if (!status) return;
      status.removeAttribute('aria-hidden');

      if (!open || !input.value.trim()) {
        status.textContent = '';
        return;
      }
      status.textContent = count > 0
        ? count + (count === 1 ? ' result' : ' results') + ' available.'
        : 'No results found.';
    }, STATUS_DEBOUNCE);
  }

  function syncAllComboboxes() {
    document.querySelectorAll(COMBOBOX_SELECTOR).forEach(syncCombobox);
  }

  scan(document);
  syncAllComboboxes();

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(scan);
    });
    syncAllComboboxes();
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('keydown', function (e) {
    if (e.repeat) return;

    if (e.key === 'Escape' || e.code === 'Escape') {
      document.querySelectorAll(COMBOBOX_SELECTOR).forEach(resetCombobox);
      return;
    }

    if (e.key !== 'Enter' && e.code !== 'Enter' && e.key !== ' ' && e.code !== 'Space') return;
    var target = e.target;
    if (!target || !target.matches || !target.matches(SUGGESTION_SELECTOR)) return;
    e.preventDefault();
    (target.querySelector('[data-nosto-element="keyword"]') || target).click();
  }, true);

  // Native <details> popdowns don't fire childList mutations on close.
  document.addEventListener('toggle', function (e) {
    var details = e.target;
    if (!details || details.tagName !== 'DETAILS' || details.open) return;
    details.querySelectorAll(COMBOBOX_SELECTOR).forEach(resetCombobox);
  }, true);
})();
